import { useMemo } from "react";
import { useInfiniteQuery, type InfiniteData } from "@tanstack/react-query";
import { useApiConfig } from "@visioni/a3s-client-api/app";
import { expandEndpoint } from "@visioni/a3s-client-api";
import { AssetCameraKey, AssetId, CameraId } from "@visioni/a3s-schema";
import { match } from "ts-pattern";
import type { ActivityFilters } from "../activity-filter-params";
import type { ListActivityResponse, ListActivityQuery } from "../../client-messages/list-activity";
import { listActivityEndpoint } from "../../client-messages/list-activity";
import type { ActivityItemViewModel } from "../components/ActivityItemCard/ActivityItemCard";

// ---------------------------------------------------------------------------
// Options
// ---------------------------------------------------------------------------

export interface UseActivityListOptions {
  filters: ActivityFilters;
  /** Map of camera key → display name, used to resolve labels for list items. */
  cameraNames?: ReadonlyMap<string, string>;
  /** Map of site ID → display name, used for site-scoped permission items. */
  siteNames?: ReadonlyMap<string, string>;
  /** Formats a raw camera key into a display label when not found in cameraNames. */
  formatCameraKey?: ((key: string) => string) | undefined;
  pageSize?: number;
  enabled?: boolean;
}

// ---------------------------------------------------------------------------
// Result
// ---------------------------------------------------------------------------

export interface ActivityListResult {
  readonly items: readonly ActivityItemViewModel[];
  readonly totalCount: number;
  readonly isLoading: boolean;
  readonly isFetchingNextPage: boolean;
  readonly hasNextPage: boolean;
  readonly error: Error | null;
  readonly fetchNextPage: () => void;
}

// ---------------------------------------------------------------------------
// View model mapping
// ---------------------------------------------------------------------------

export function toViewModel(
  item: ListActivityResponse["data"][number],
  cameraNames?: ReadonlyMap<string, string>,
  siteNames?: ReadonlyMap<string, string>,
  formatCameraKey?: (key: string) => string,
): ActivityItemViewModel {
  const rawCameraKey = buildCameraKey(item.assetId, item.cameraId);
  const isKnownCamera = rawCameraKey != null && cameraNames?.has(rawCameraKey);
  return {
    id: item.id,
    summary: formatPermissionSummary(item) ?? item.summary,
    createdAt: item.createdAt,
    cameraKey: isKnownCamera ? rawCameraKey : null,
    cameraLabel: resolveScopeLabel(item, rawCameraKey, cameraNames, siteNames, formatCameraKey),
    userEmail: item.actorEmail ?? systemActorLabel(item.actorId),
    kind: item.kind,
    hasDetails: isExpandableKind(item.kind),
    data: item.data ?? null,
  };
}

function formatPermissionSummary(item: ListActivityResponse["data"][number]): string | null {
  const data = item.data as Record<string, unknown> | null | undefined;
  if (!data) return null;

  if (item.kind === "user_permission_granted") {
    const target = (data["grantedToEmail"] as string | null) ?? (data["grantedToUserId"] as string | null) ?? "user";
    return `Granted ${permissionScopeLabel(data)} to ${target}`;
  }

  if (item.kind === "user_permission_revoked") {
    const target =
      (data["revokedFromEmail"] as string | null) ?? (data["revokedFromUserId"] as string | null) ?? "user";
    return `Revoked ${permissionScopeLabel(data)} from ${target}`;
  }

  return null;
}

function permissionScopeLabel(data: Record<string, unknown>): string {
  const cameraRole = data["cameraRole"] as string | null;
  if (cameraRole) {
    return match(cameraRole)
      .with("camera_viewer", () => "camera viewer access")
      .with("camera_manager", () => "camera manager access")
      .otherwise(() => "camera access");
  }

  return match(data["scopeType"] as string | null)
    .with("site", () => "site access")
    .with("tenant", () => "admin access")
    .otherwise(() => "access");
}

function resolveScopeLabel(
  item: ListActivityResponse["data"][number],
  cameraKey: string | null,
  cameraNames?: ReadonlyMap<string, string>,
  siteNames?: ReadonlyMap<string, string>,
  formatCameraKey?: (key: string) => string,
): string | null {
  if (cameraKey) {
    const known = cameraNames?.get(cameraKey);
    if (known) return known;
    return formatCameraKey ? formatCameraKey(cameraKey) : cameraKey;
  }
  if (item.siteId && siteNames?.has(item.siteId)) return siteNames.get(item.siteId) ?? item.siteId;
  if (item.siteId) return item.siteId;
  return null;
}

/**
 * Human label for a system actor (e.g. `system:hub_sync`), so system-attributed rows
 * (hub sync, CLI) don't render as a blank "—" author. Real user actors always resolve
 * to an email via `actorEmail` and never reach this function.
 */
function systemActorLabel(actorId: string | null): string | null {
  if (!actorId?.startsWith("system:")) return null;
  const source = actorId.slice("system:".length);
  return match(source)
    .with("hub_sync", () => "System (hub sync)")
    .with("cli", () => "System (CLI)")
    .otherwise(() => `System (${source})`);
}

function buildCameraKey(assetId: string | null, cameraId: string | null): string | null {
  if (!assetId || !cameraId) return null;
  try {
    const key = AssetCameraKey.fromParts(AssetId.fromString(assetId), CameraId.fromString(cameraId));
    return AssetCameraKey.toString(key);
  } catch {
    return null;
  }
}

function isExpandableKind(kind: string): boolean {
  return [
    "alarm_routine_configured",
    "alarm_override_configured",
    "alarm_response_configured",
    "alarm_exception_configured",
    "alarm_timezone_configured",
  ].includes(kind);
}

// ---------------------------------------------------------------------------
// Query param mapping
// ---------------------------------------------------------------------------

function filtersToQueryParams(filters: ActivityFilters, limit: number, offset: number): ListActivityQuery {
  return {
    siteId: filters.siteId ?? undefined,
    cameraKey: filters.cameraKey ?? undefined,
    kinds: filters.kinds.length > 0 ? filters.kinds.join(",") : undefined,
    userEmail: filters.userEmail ?? undefined,
    dateFrom: filters.startDate ?? undefined,
    dateTo: filters.endDate ?? undefined,
    limit: String(limit),
    offset: String(offset),
  };
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

const DEFAULT_PAGE_SIZE = 20;

export function useActivityList(options: UseActivityListOptions): ActivityListResult {
  const apiConfig = useApiConfig();
  const { filters, cameraNames, siteNames, formatCameraKey, pageSize = DEFAULT_PAGE_SIZE, enabled = true } = options;
  const { tenant, authenticatedFetch } = apiConfig;

  const query = useInfiniteQuery<
    ListActivityResponse,
    Error,
    InfiniteData<ListActivityResponse, number>,
    readonly unknown[],
    number
  >({
    queryKey: ["activity", "list", tenant?.tenantId, filters, pageSize] as const,
    enabled: enabled && !!tenant,
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((sum, page) => sum + page.data.length, 0);
      return loaded < lastPage.totalCount ? loaded : undefined;
    },
    queryFn: async ({ pageParam, signal }) => {
      if (!tenant) throw new Error("Tenant not ready");

      const queryParams = filtersToQueryParams(filters, pageSize, pageParam);
      const url =
        tenant.apiBaseUrl +
        expandEndpoint(listActivityEndpoint, { path: { tenantId: tenant.tenantId }, query: queryParams });

      const response = await authenticatedFetch(url, { signal });
      if (!response.ok) {
        let message = `Request failed: ${String(response.status)}`;
        try {
          const text = await response.text();
          if (text) {
            try {
              const json = JSON.parse(text) as { message?: string };
              if (json.message) message = json.message;
              else message = text;
            } catch {
              message = text;
            }
          }
        } catch {
          // fall through with default message
        }
        throw new Error(message);
      }

      const data: unknown = await response.json();
      return listActivityEndpoint.responseSchema.parse(data);
    },
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    staleTime: 0,
  });

  const items = useMemo<readonly ActivityItemViewModel[]>(
    () =>
      query.data?.pages.flatMap((page) =>
        page.data.map((item) => toViewModel(item, cameraNames, siteNames, formatCameraKey)),
      ) ?? [],
    [query.data, cameraNames, siteNames, formatCameraKey],
  );

  const totalCount = query.data?.pages[0]?.totalCount ?? 0;

  return {
    items,
    totalCount,
    isLoading: query.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage,
    error: query.error instanceof Error ? query.error : null,
    fetchNextPage: () => {
      void query.fetchNextPage();
    },
  };
}
